import { siteConfig } from "@/mocks/siteConfig";

interface SEOSchemaProps {
  type: "home" | "category" | "article" | "about";
  categoryName?: string;
  categorySlug?: string;
  articleData?: {
    title: string;
    slug: string;
    excerpt: string;
    author: string;
    date: string;
    coverImage: string;
  };
}

export default function SEOSchema({ type, categoryName, categorySlug, articleData }: SEOSchemaProps) {
  const baseSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteConfig.name,
      url: siteConfig.url,
      description: siteConfig.description,
      email: siteConfig.email,
      telephone: siteConfig.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: siteConfig.address,
        addressCountry: "CN",
      },
      foundingDate: siteConfig.foundedYear.toString(),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
      description: siteConfig.description,
      potentialAction: {
        "@type": "SearchAction",
        target: `${siteConfig.url}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];

  const getPageSchema = () => {
    switch (type) {
      case "home":
        return {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: `${siteConfig.name} - ${siteConfig.tagline}`,
          description: siteConfig.description,
          url: siteConfig.url,
          isPartOf: {
            "@type": "WebSite",
            name: siteConfig.name,
            url: siteConfig.url,
          },
        };
      case "category":
        return {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${categoryName} - ${siteConfig.name}`,
          description: `浏览${siteConfig.name}平台上关于${categoryName}的所有文章和教程`,
          url: `${siteConfig.url}/category/${categorySlug}`,
          isPartOf: {
            "@type": "WebSite",
            name: siteConfig.name,
            url: siteConfig.url,
          },
        };
      case "article":
        return articleData
          ? {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: articleData.title,
              description: articleData.excerpt,
              image: articleData.coverImage,
              author: {
                "@type": "Organization",
                name: articleData.author,
              },
              datePublished: articleData.date,
              publisher: {
                "@type": "Organization",
                name: siteConfig.name,
              },
              url: `${siteConfig.url}/article/${articleData.slug}`,
              isPartOf: {
                "@type": "WebSite",
                name: siteConfig.name,
                url: siteConfig.url,
              },
            }
          : null;
      case "about":
        return {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: `关于我们 - ${siteConfig.name}`,
          description: `了解${siteConfig.name}的品牌故事、发展历程、核心团队与联系方式`,
          url: `${siteConfig.url}/about`,
          isPartOf: {
            "@type": "WebSite",
            name: siteConfig.name,
            url: siteConfig.url,
          },
        };
      default:
        return null;
    }
  };

  const pageSchema = getPageSchema();
  const allSchemas = pageSchema ? [...baseSchemas, pageSchema] : baseSchemas;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }}
    />
  );
}